import type {
  QuantityDiscountFieldsConfig,
  QuantityDiscountSurfaceConfig,
  QuantityDiscountTierDTO
} from 'ecosystem'
import type { QuantityDiscountSummary } from 'shared-utils'
import { formatAmount } from 'shared-utils'

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}

export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] as string)

/**
 * Percentages arrive as doubles, so `5.0` must read as "5%" and `7.5` as
 * "7.5%" — neither "5.0%" nor a rounded "8%".
 */
const percent = (value: number): string => `${Number(value.toFixed(2))}%`

export interface TeaserTemplateContext {
  summary: QuantityDiscountSummary
  /** The per-unit price the ladder is scored against, in the active VAT mode. */
  unitPrice: number
  productName: string
  currency: string
  /**
   * Unit abbreviation for "per piece" — "st", "pcs", "kpl". A token rather than
   * a word baked into each sentence, so a translator can place it wherever
   * their language wants it. Defaults to the Swedish "st".
   */
  unitAbbr?: string
}

/** Swedish "styck", matching the rest of the storefront's hardcoded defaults. */
const DEFAULT_UNIT_ABBR = 'st'

const tierPill = (tier: QuantityDiscountTierDTO): string =>
  `<span class="qd-tier">${tier.minQuantity}+ · −${escapeHtml(percent(tier.percentage))}</span>`

/**
 * Placeholder values are HTML-escaped before substitution, so a merchant-authored
 * template can only inject markup they wrote themselves. `ParsedHtml` sanitises
 * the assembled result again on render.
 */
const templateValues = ({
  summary,
  unitPrice,
  productName,
  currency,
  unitAbbr
}: TeaserTemplateContext): Record<string, string> => {
  const { currentTier, nextTier, mode } = summary
  const money = (value: number) => `${formatAmount(value)} ${currency}`
  const nextUnitPrice = nextTier ? unitPrice * (1 - nextTier.percentage / 100) : unitPrice

  return {
    productName: escapeHtml(productName),
    mode: mode ?? '',
    unit: escapeHtml(unitAbbr || DEFAULT_UNIT_ABBR),
    unitPrice: escapeHtml(money(unitPrice)),

    // What is already won
    currentQty: currentTier ? String(currentTier.minQuantity) : '',
    currentPercentage: currentTier ? escapeHtml(percent(currentTier.percentage)) : '',
    discountedUnits: String(summary.discountedUnits),
    fullPriceUnits: String(summary.fullPriceUnits),

    // What is one step away
    nextQty: nextTier ? String(nextTier.minQuantity) : '',
    nextPercentage: nextTier ? escapeHtml(percent(nextTier.percentage)) : '',
    unitsToNext: summary.unitsToNext ? String(summary.unitsToNext) : '',
    nextUnitPrice: nextTier ? escapeHtml(money(nextUnitPrice)) : '',
    /** Per-unit saving at the next tier — the number that actually motivates. */
    nextSaving: nextTier ? escapeHtml(money(unitPrice - nextUnitPrice)) : '',

    // The offer as a whole
    bestQty: summary.bestTier ? String(summary.bestTier.minQuantity) : '',
    bestPercentage: summary.bestTier ? escapeHtml(percent(summary.bestTier.percentage)) : '',
    /** Under PACK a tier's `minQuantity` is a block size, which reads differently. */
    packSize: mode === 'PACK' && nextTier ? String(nextTier.minQuantity) : '',
    tiers: summary.tiers.map(tierPill).join('')
  }
}

/**
 * `\s*` also has to absorb `&nbsp;`: a template pasted out of a rich-text editor
 * comes back with non-breaking spaces inside the braces, which would otherwise
 * make the placeholder silently render as literal text.
 */
const PLACEHOLDER = /\{\{(?:\s|&nbsp;)*(\w+)(?:\s|&nbsp;)*\}\}/g

export const renderTemplate = (template: string, context: TeaserTemplateContext): string => {
  const values = templateValues(context)

  // A replacer function is required, not a replacement string: values may contain
  // `$&`-style sequences that String.replace would otherwise expand.
  return template.replace(PLACEHOLDER, (match, key: string) =>
    key in values ? (values[key] as string) : match
  )
}

const DEFAULT_FIELDS: QuantityDiscountFieldsConfig = {
  nextUnitPrice: false,
  saving: false,
  tiers: false
}

/**
 * Override keys for the built-in copy.
 *
 * The sentences below are Swedish, like every other hardcoded default in the
 * storefront, and every one of them is replaceable per store through the
 * `translations/<language>.json` dictionaries that become `appConfig.labels` at
 * build time. Without that a Danish store with no authored template would read
 * Swedish — the merchant-facing template editor is an opt-in refinement, not
 * the mechanism that makes this feature multilingual.
 *
 * The markup and the `{{token}}`s stay out of the dictionary: a translator
 * supplies a sentence, and the `<strong>` wrapping is applied here.
 */
export interface QuantityDiscountTeaserOverrides {
  /** Below the first tier, THRESHOLD: "Buy {{nextQty}} and save {{nextPercentage}} on the whole order." */
  quantityDiscountTeaser?: string
  /** Below the first tier, PACK: the saving repeats per pack rather than covering the order. */
  quantityDiscountTeaserPack?: string
  /** A tier is active and a better one is reachable. */
  quantityDiscountProgress?: string
  /** Same, under PACK: the next block is what is being offered. */
  quantityDiscountProgressPack?: string
  /** Top tier reached — nothing left to unlock. */
  quantityDiscountAchieved?: string
  /** Suffix on the per-unit saving line. */
  quantityDiscountSavingPerUnit?: string
  /** Unit abbreviation in the per-unit price and saving lines ("st", "pcs", "kpl"). */
  quantityDiscountUnitAbbr?: string
}

type TeaserLabels = Partial<Record<keyof QuantityDiscountTeaserOverrides, string>>

/**
 * Copy used when the merchant has not authored a template for this state.
 *
 * `labels` carries the store's dictionary (`appConfig.labels`, keyed by
 * {@link QuantityDiscountTeaserOverrides}); anything missing falls back to the
 * Swedish default, which is the same contract `overrideText` has everywhere
 * else. The admin preview passes nothing and therefore shows the Swedish base.
 */
export const buildDefaultTemplate = (
  state: QuantityDiscountSummary['state'],
  mode: QuantityDiscountSummary['mode'],
  fields: QuantityDiscountFieldsConfig = DEFAULT_FIELDS,
  labels: TeaserLabels = {}
): string => {
  if (state === 'NONE') return ''

  const lead = (() => {
    switch (state) {
      case 'ACHIEVED':
        return (
          labels.quantityDiscountAchieved || 'Du sparar {{currentPercentage}} på den här varan.'
        )
      case 'PROGRESS':
        return mode === 'PACK'
          ? labels.quantityDiscountProgressPack ||
              'Köp {{unitsToNext}} till och få {{nextPercentage}} rabatt på ytterligare {{nextQty}} {{unit}}.'
          : labels.quantityDiscountProgress ||
              'Köp {{unitsToNext}} till och spara {{nextPercentage}} på hela ordern.'
      default:
        return mode === 'PACK'
          ? labels.quantityDiscountTeaserPack ||
              'Köp {{nextQty}} {{unit}} och spara {{nextPercentage}} på varje förpackning.'
          : labels.quantityDiscountTeaser ||
              'Köp {{nextQty}} {{unit}} och spara {{nextPercentage}} på hela ordern.'
    }
  })()

  const saving = labels.quantityDiscountSavingPerUnit || 'Du sparar {{nextSaving}}/{{unit}}'

  return [
    `<p><strong>${lead}</strong></p>`,
    fields.nextUnitPrice && state !== 'ACHIEVED'
      ? '<p><small>{{nextUnitPrice}}/{{unit}}</small></p>'
      : '',
    fields.saving && state !== 'ACHIEVED' ? `<p><small>${saving}</small></p>` : '',
    fields.tiers ? '<p>{{tiers}}</p>' : ''
  ]
    .filter(Boolean)
    .join('')
}

/**
 * Resolve the markup for one surface in one state.
 *
 * Returns an empty string when there is nothing to say: no ladder, or the
 * surface switched off.
 *
 * **An empty template means "use the built-in copy", not "render nothing".**
 * The earlier rule — missing falls back, empty renders nothing — could not
 * survive contact with the editor: a merchant has no way to express "absent" as
 * opposed to "cleared", and a code editor mounted on an unset field writes an
 * empty string into the form anyway. Every untouched state then silently
 * rendered nothing, which is the opposite of what the help text promised. So
 * whitespace-only is treated as unset.
 */
export const teaserContent = (
  config: QuantityDiscountSurfaceConfig | undefined,
  context: TeaserTemplateContext,
  /** The store's label dictionary, so the built-in copy is not stuck in Swedish. */
  labels: TeaserLabels = {}
): string => {
  const { state, mode } = context.summary

  if (!config?.enabled || state === 'NONE') {
    return ''
  }

  // Same keys index both maps, so a state can never read one surface's template
  // while obeying another's switch.
  const stateKey = ({ BELOW_FIRST: 'teaser', PROGRESS: 'progress', ACHIEVED: 'achieved' } as const)[
    state
  ]

  // Undefined is on: configurations authored before per-state switches existed
  // must keep saying everything they said before.
  if (config.states?.[stateKey] === false) {
    return ''
  }

  const authored = config.templates?.[stateKey]

  const template = authored?.trim()
    ? authored
    : buildDefaultTemplate(state, mode, config.fields, labels)

  return renderTemplate(
    template,
    // An explicit unit on the context wins (the admin preview drives it
    // directly); otherwise it comes from the store's dictionary.
    { ...context, unitAbbr: context.unitAbbr ?? labels.quantityDiscountUnitAbbr }
  )
    .replace(/<p>\s*<\/p>/g, '')
    .replace(/<small>\s*<\/small>/g, '')
    .trim()
}
